import React from "react";
import { useRouter } from "next/router";
import styled, { createGlobalStyle } from "styled-components";
import { FlexContainer, FlexDiv } from "@/components/Atoms/Box";
import { Typography as DefaultTypography } from "@/foundation/Typography/V1";
import theme from "@/foundation/Theme";
import { space } from "styled-system";
import useDisableIntercom from "@/hooks/useDisableIntercom";

export const EndUserLicenseAgreementPage = (props: any) => {
  const { showHeader = false } = props || {};

  const location = useRouter();

  const isFromAppSource = location?.query;

  const showTopSpace = isFromAppSource?.source !== "app";

  return (
    <Container flexDirection={"column"}>
      {showHeader && (
        <Typography
          fontFamily={"var(--man-500)"}
          color={theme.colors.gray_100}
          variant="heading1"
        >
          END USER LICENCE AGREEMENT
        </Typography>
      )}
      <Wrapper flexDirection={"column"} showHeightGap={showTopSpace}>
        <FlexDiv style={{ gap: "10px", marginBottom: "20px" }}>
          <Typography
            fontFamily={"var(--man-600)"}
            color={theme.colors.gray_100}
            variant="paragraph2"
            style={{
              fontStyle: "italic",
            }}
          >
            Last updated:
          </Typography>
          <Typography
            fontFamily={"var(--man-500)"}
            color={theme.colors.gray_100}
            variant="paragraph2"
          >
            February 06, 2024
          </Typography>
        </FlexDiv>

        <Typography
          fontFamily="var(--man-500)"
          variant="paragraph2"
          color={theme.colors.gray_100}
        >
          Shyft Health is licensed to You (End-User) by Blacktail Mindhouse Pvt.
          Ltd., located and registered at{" "}
          <Address>
            {" "}
            91 Springboard Business HUB Pvt Ltd, B1/H3, Mohan Co-Operative
            Industrial Area, Mathura Rd, Block B, New Delhi, Delhi 110044, India{" "}
          </Address>
          ('Licensor'), for use only under the terms of this Licence Agreement.
        </Typography>
        <Typography variant="paragraph2" color={theme.colors.gray_100}>
          By downloading the Licensed Application from Apple's software
          distribution platform ('App Store') and Google's software distribution
          platform ('Play Store'), and any update thereto (as permitted by this
          Licence Agreement), You indicate that You agree to be bound by all of
          the terms and conditions of this Licence Agreement, and that You
          accept this Licence Agreement. App Store and Play Store are referred
          to in this Licence Agreement as 'Services'.
        </Typography>
        <Typography variant="paragraph2" color={theme.colors.gray_100}>
          The parties of this Licence Agreement acknowledge that the Services
          are not a Party to this Licence Agreement and are not bound by any
          provisions or obligations with regard to the Licensed Application,
          such as warranty, liability, maintenance and support thereof.
          Blacktail Mindhouse Pvt. Ltd., not the Services, is solely responsible
          for the Licensed Application and the content thereof.
        </Typography>
        <Typography variant="paragraph2" color={theme.colors.gray_100}>
          This Licence Agreement may not provide for usage rules for the
          Licensed Application that are in conflict with the latest Apple Media
          Services Terms and Conditions and Google Play Terms of Service ('Usage
          Rules'). Blacktail Mindhouse Pvt. Ltd. acknowledges that it had the
          opportunity to review the Usage Rules and this Licence Agreement is
          not conflicting with them.
        </Typography>
        <Typography variant="paragraph2" color={theme.colors.gray_100}>
          Shyft Health when purchased or downloaded through the Services, is
          licensed to You for use only under the terms of this Licence
          Agreement. The Licensor reserves all rights not expressly granted to
          You. Shyft Health is to be used on devices that operate with Apple's
          operating systems ('iOS' and 'Mac OS') or Google's operating system
          ('Android').
        </Typography>

        <Heading variant="paragraph2" color={theme.colors.gray_100}>
          TABLE OF CONTENTS
        </Heading>
        <OL mb={"25px"}>
          <LI>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              THE APPLICATION
            </Typography>
          </LI>
          <LI>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              SCOPE OF LICENCE
            </Typography>
          </LI>
          <LI>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              TECHNICAL REQUIREMENTS
            </Typography>
          </LI>
          <LI>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              MAINTENANCE AND SUPPORT
            </Typography>
          </LI>
          <LI>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              USE OF DATA
            </Typography>
          </LI>
          <LI>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              USER-GENERATED CONTRIBUTIONS
            </Typography>
          </LI>
          <LI>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              CONTRIBUTION LICENCE
            </Typography>
          </LI>
          <LI>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              LIABILITY
            </Typography>
          </LI>
          <LI>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              WARRANTY
            </Typography>
          </LI>

          <LI>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              PRODUCT CLAIMS
            </Typography>
          </LI>

          <LI>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              LEGAL COMPLIANCE
            </Typography>
          </LI>

          <LI>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              CONTACT INFORMATION
            </Typography>
          </LI>

          <LI>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              TERMINATION
            </Typography>
          </LI>

          <LI>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              THIRD-PARTY TERMS OF AGREEMENTS AND BENEFICIARY
            </Typography>
          </LI>

          <LI>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              INTELLECTUAL PROPERTY RIGHTS
            </Typography>
          </LI>

          <LI>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              APPLICABLE LAW
            </Typography>
          </LI>

          <LI>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              MISCELLANEOUS
            </Typography>
          </LI>
        </OL>
        <OL mb={"25px"}>
          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              THE APPLICATION
            </Heading>

            <OL mb={"25px"}>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Shyft Health ('Licensed Application') is a piece of software
                  created to This EULA applies to all users of Shyft Community,
                  including health and nutrition specialists, enthusiasts, and
                  any other individuals who access the platform. — and
                  customised for iOS and Android mobile devices ('Devices'). It
                  is used to This End-User License Agreement (EULA) governs the
                  use of Shyft Community, a social media platform designed for
                  health and nutrition specialists and enthusiasts. By accessing
                  or using Shyft Community, you agree to comply with the terms
                  and conditions outlined in this EULA. .
                </Typography>

                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  The Licensed Application is not tailored to comply with
                  industry-specific regulations (Health Insurance Portability
                  and Accountability Act (HIPAA), Federal Information Security
                  Management Act (FISMA), etc.), so if your interactions would
                  be subjected to such laws, you may not use this Licensed
                  Application. You may not use the Licensed Application in a way
                  that would violate the Gramm-Leach-Bliley Act (GLBA).
                </Typography>
              </LI>
            </OL>
          </LI>
          {/* @@@@@@@@@ */}

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              SCOPE OF LICENCE
            </Heading>

            <OL mb={"25px"}>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  You are given a non-transferable, non-exclusive,
                  non-sublicensable licence to install and use the Licensed
                  Application on any Devices that You (End-User) own or control
                  and as permitted by the Usage Rules, with the exception that
                  such Licensed Application may be accessed and used by other
                  accounts associated with You (End-User, The Purchaser) via
                  Family Sharing or volume purchasing.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  This licence will also govern any updates of the Licensed
                  Application provided by Licensor that replace, repair, and/or
                  supplement the first Licensed Application, unless a separate
                  licence is provided for such update, in which case the terms
                  of that new licence will govern.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  You may not share or make the Licensed Application available
                  to third parties (unless to the degree allowed by the Usage
                  Rules, and with Blacktail Mindhouse Pvt. Ltd.'s prior written
                  consent), sell, rent, lend, lease or otherwise redistribute
                  the Licensed Application.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  You may not reverse engineer, translate, disassemble,
                  integrate, decompile, remove, modify, combine, create
                  derivative works or updates of, adapt, or attempt to derive
                  the source code of the Licensed Application, or any part
                  thereof (except with Blacktail Mindhouse Pvt. Ltd.'s prior
                  written consent).
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  You may not copy (excluding when expressly authorised by this
                  licence and the Usage Rules) or alter the Licensed Application
                  or portions thereof. You may create and store copies only on
                  devices that You own or control for backup keeping under the
                  terms of this licence, the Usage Rules, and any other terms
                  and conditions that apply to the device or software used. You
                  may not remove any intellectual property notices. You
                  acknowledge that no unauthorised third parties may gain access
                  to these copies at any time. If you sell your Devices to a
                  third party, you must remove the Licensed Application from the
                  Devices before doing so.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Violations of the obligations mentioned above, as well as the
                  attempt of such infringement, may be subject to prosecution
                  and damages.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Licensor reserves the right to modify the terms and conditions
                  of licensing.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Nothing in this licence should be interpreted to restrict
                  third-party terms. When using the Licensed Application, You
                  must ensure that You comply with applicable third-party terms
                  and conditions.
                </Typography>
              </LI>
            </OL>
          </LI>

          {/* 222 */}

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              TECHNICAL REQUIREMENTS
            </Heading>

            <OL mb={"25px"}>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Licensor attempts to keep the Licensed Application updated so
                  that it complies with modified/new versions of the firmware
                  and new hardware. You are not granted rights to claim such an
                  update.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  You acknowledge that it is Your responsibility to confirm and
                  determine that the app end-user device on which You intend to
                  use the Licensed Application satisfies the technical
                  specifications mentioned above.
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Licensor reserves the right to modify the technical
                  specifications as it sees appropriate at any time.
                </Typography>
              </LI>
            </OL>
          </LI>

          {/*  */}

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              MAINTENANCE AND SUPPORT
            </Heading>

            <OL mb={"25px"}>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  The Licensor is solely responsible for providing any
                  maintenance and support services for this Licensed
                  Application. You can reach the Licensor at the email address
                  listed in the App Store or Play Store Overview for this
                  Licensed Application.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Blacktail Mindhouse Pvt. Ltd. and the End-User acknowledge
                  that the Services have no obligation whatsoever to furnish any
                  maintenance and support services with respect to the Licensed
                  Application.
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              USE OF DATA
            </Heading>

            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              You acknowledge that Licensor will be able to access and adjust
              Your downloaded Licensed Application content and Your personal
              information, and that Licensor's use of such material and
              information is subject to Your legal agreements with Licensor and
              Licensor's privacy policy:{" "}
              <Typography
                fontFamily={"var(--man-500)"}
                variant="link1"
                link1Color={theme.colorsV2.blue.shade_700}
                href="https://www.betheshyft.com/privacy-policy/"
              >
                https://www.betheshyft.com/privacy-policy/.
              </Typography>
            </Typography>

            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              You acknowledge that the Licensor may periodically collect and use
              technical data and related information about your device, system,
              and application software, and peripherals, offer product support,
              facilitate the software updates, and for purposes of providing
              other services to you (if any) related to the Licensed
              Application. Licensor may also use this information to improve its
              products or to provide services or technologies to you, as long as
              it is in a form that does not personally identify you.
            </Typography>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              USER-GENERATED CONTRIBUTIONS
            </Heading>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              The Licensed Application may invite you to chat, contribute to, or
              participate in blogs, message boards, online forums, and other
              functionality, and may provide you with the opportunity to create,
              submit, post, display, transmit, perform, publish, distribute, or
              broadcast content and materials to us or in the Licensed
              Application, including but not limited to text, writings, video,
              audio, photographs, graphics, comments, suggestions, or personal
              information or other material (collectively, 'Contributions').
              Contributions may be viewable by other users of the Licensed
              Application and through third-party websites or applications. As
              such, any Contributions you transmit may be treated as
              non-confidential and non-proprietary. When you create or make
              available any Contributions, you thereby represent and warrant
              that:
            </Typography>

            <OL mb={"25px"}>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  The creation, distribution, transmission, public display, or
                  performance, and the accessing, downloading, or copying of
                  your Contributions do not and will not infringe the
                  proprietary rights, including but not limited to the
                  copyright, patent, trademark, trade secret, or moral rights of
                  any third party.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  You are the creator and owner of or have the necessary
                  licences, rights, consents, releases, and permissions to use
                  and to authorise us, the Licensed Application, and other users
                  of the Licensed Application to use your Contributions in any
                  manner contemplated by the Licensed Application and this
                  Licence Agreement.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  You have the written consent, release, and/or permission of
                  each and every identifiable individual person in your
                  Contributions to use the name or likeness or each and every
                  such identifiable individual person to enable inclusion and
                  use of your Contributions in any manner contemplated by the
                  Licensed Application and this Licence Agreement.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Your Contributions are not false, inaccurate, or misleading.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Your Contributions are not unsolicited or unauthorised
                  advertising, promotional materials, pyramid schemes, chain
                  letters, spam, mass mailings, or other forms of solicitation.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Your Contributions are not obscene, lewd, lascivious, filthy,
                  violent, harassing, libellous, slanderous, or otherwise
                  objectionable (as determined by us).
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Your Contributions do not ridicule, mock, disparage,
                  intimidate, or abuse anyone.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Your Contributions are not used to harass or threaten (in the
                  legal sense of those terms) any other person and to promote
                  violence against a specific person or class of people.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Your Contributions do not violate any applicable law,
                  regulation, or rule.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Your Contributions do not violate the privacy or publicity
                  rights of any third party.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Your Contributions do not violate any applicable law
                  concerning child pornography, or otherwise intended to protect
                  the health or well-being of minors.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Your Contributions do not include any offensive comments that
                  are connected to race, national origin, gender, sexual
                  preference, or physical handicap.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Your Contributions do not otherwise violate, or link to
                  material that violates, any provision of this Licence
                  Agreement, or any applicable law or regulation.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Any use of the Licensed Application in violation of the
                  foregoing violates this Licence Agreement and may result in,
                  among other things, termination or suspension of your rights
                  to use the Licensed Application.
                </Typography>
              </LI>
            </OL>
          </LI>
          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              CONTRIBUTION LICENCE
            </Heading>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              By posting your Contributions to any part of the Licensed
              Application or making Contributions accessible to the Licensed
              Application by linking your account from the Licensed Application
              to any of your social networking accounts, you automatically
              grant, and you represent and warrant that you have the right to
              grant, to us an unrestricted, unlimited, irrevocable, perpetual,
              non-exclusive, transferable, royalty-free, fully-paid, worldwide
              right, and licence to host, use copy, reproduce, disclose, sell,
              resell, publish, broad cast, retitle, archive, store, cache,
              publicly display, reformat, translate, transmit, excerpt (in whole
              or in part), and distribute such Contributions (including, without
              limitation, your image and voice) for any purpose, commercial
              advertising, or otherwise, and to prepare derivative works of, or
              incorporate in other works, such as Contributions, and grant and
              authorise sublicences of the foregoing. The use and distribution
              may occur in any media formats and through any media channels.
            </Typography>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              This licence will apply to any form, media, or technology now
              known or hereafter developed, and includes our use of your name,
              company name, and franchise name, as applicable, and any of the
              trademarks, service marks, trade names, logos, and personal and
              commercial images you provide. You waive all moral rights in your
              Contributions, and you warrant that moral rights have not
              otherwise been asserted in your Contributions.
            </Typography>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              We do not assert any ownership over your Contributions. You retain
              full ownership of all of your Contributions and any intellectual
              property rights or other proprietary rights associated with your
              Contributions. We are not liable for any statements or
              representations in your Contributions provided by you in any area
              in the Licensed Application. You are solely responsible for your
              Contributions to the Licensed Application and you expressly agree
              to exonerate us from any and all responsibility and to refrain
              from any legal action against us regarding your Contributions.
            </Typography>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              We have the right, in our sole and absolute discretion, (1) to
              edit, redact, or otherwise change any Contributions; (2) to
              recategorise any Contributions to place them in more appropriate
              locations in the Licensed Application; and (3) to prescreen or
              delete any Contributions at any time and for any reason, without
              notice. We have no obligation to monitor your Contributions.
            </Typography>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              LIABILITY
            </Heading>
            <OL mb={"25px"}>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Licensor's responsibility in the case of violation of
                  obligations and tort shall be limited to intent and gross
                  negligence. Only in case of a breach of essential contractual
                  duties (cardinal obligations), Licensor shall also be liable
                  in case of slight negligence. In any case, liability shall be
                  limited to the foreseeable, contractually typical damages. The
                  limitation mentioned above does not apply to injuries to life,
                  limb, or health.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Licensor takes no accountability or responsibility for any
                  damages caused due to a breach of duties according to Section
                  2 of this Licence Agreement. To avoid data loss, You are
                  required to make use of backup functions of the Licensed
                  Application to the extent allowed by applicable third-party
                  terms and conditions of use. You are aware that in case of
                  alterations or manipulations of the Licensed Application, You
                  will not have access to the Licensed Application.
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              WARRANTY
            </Heading>
            <OL mb={"25px"}>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  Licensor warrants that the Licensed Application is free of
                  spyware, trojan horses, viruses, or any other malware at the
                  time of Your download. Licensor warrants that the Licensed
                  Application works as described in the user documentation.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  No warranty is provided for the Licensed Application that is
                  not executable on the device, that has been unauthorisedly
                  modified, handled inappropriately or culpably, combined or
                  installed with inappropriate hardware or software, used with
                  inappropriate accessories, regardless if by Yourself or by
                  third parties, or if there are any other reasons outside of
                  Blacktail Mindhouse Pvt. Ltd.'s sphere of influence that
                  affect the executability of the Licensed Application.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  You are required to inspect the Licensed Application
                  immediately after installing it and notify Blacktail Mindhouse
                  Pvt. Ltd. about issues discovered without delay by email
                  provided in Contact Information. The defect report will be
                  taken into consideration and further investigated if it has
                  been emailed within a period of thirty (30) days after
                  discovery.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  If we confirm that the Licensed Application is defective,
                  Blacktail Mindhouse Pvt. Ltd. reserves a choice to remedy the
                  situation either by means of solving the defect or substitute
                  delivery.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  In the event of any failure of the Licensed Application to
                  conform to any applicable warranty, You may notify the
                  Services Store Operator, and Your Licensed Application
                  purchase price will be refunded to You. To the maximum extent
                  permitted by applicable law, the Services Store Operator will
                  have no other warranty obligation whatsoever with respect to
                  the Licensed Application, and any other losses, claims,
                  damages, liabilities, expenses, and costs attributable to any
                  negligence to adhere to any warranty.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  If the user is an entrepreneur, any claim based on faults
                  expires after a statutory period of limitation amounting to
                  twelve (12) months after the Licensed Application was made
                  available to the user. The statutory periods of limitation
                  given by law apply for users who are consumers.
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              PRODUCT CLAIMS
            </Heading>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Blacktail Mindhouse Pvt. Ltd. and the End-User acknowledge that
              Blacktail Mindhouse Pvt. Ltd., and not the Services, is
              responsible for addressing any claims of the End-User or any third
              party relating to the Licensed Application or the End-User’s
              possession and/or use of that Licensed Application, including, but
              not limited to:
            </Typography>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  product liability claims;
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  any claim that the Licensed Application fails to conform to
                  any applicable legal or regulatory requirement; and
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  claims arising under consumer protection, privacy, or similar
                  legislation, including in connection with Your Licensed
                  Application’s use of the HealthKit and HomeKit.
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              LEGAL COMPLIANCE
            </Heading>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              You represent and warrant that You are not located in a country
              that is subject to a US Government embargo, or that has been
              designated by the US Government as a 'terrorist supporting'
              country; and that You are not listed on any US Government list of
              prohibited or restricted parties.
            </Typography>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              CONTACT INFORMATION
            </Heading>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              For general inquiries, complaints, questions or claims concerning
              the Licensed Application, please contact:
            </Typography>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              <Address>
                <Strong>Tarun Gandhi</Strong>
                <Typography
                  fontFamily={"var(--man-500)"}
                  variant="link1"
                  link1Color={theme.colorsV2.blue.shade_600}
                  href="mailto:tarun.gandhi@betheshyft.com"
                >
                  (tarun.gandhi@betheshyft.com)
                </Typography>
                <br />
                91 Springboard Business HUB Pvt Ltd, B1/H3, Mohan Co-Operative
                Industrial Area, Mathura Rd, Block B, New Delhi, Delhi 110044,
                India
              </Address>
            </Typography>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              TERMINATION
            </Heading>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              The licence is valid until terminated by Blacktail Mindhouse Pvt.
              Ltd. or by You. Your rights under this licence will terminate
              automatically and without notice from Blacktail Mindhouse Pvt.
              Ltd. if You fail to adhere to any term(s) of this licence. Upon
              Licence termination, You shall stop all use of the Licensed
              Application, and destroy all copies, full or partial, of the
              Licensed Application.
            </Typography>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              THIRD-PARTY TERMS OF AGREEMENTS AND BENEFICIARY
            </Heading>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Blacktail Mindhouse Pvt. Ltd. represents and warrants that
              Blacktail Mindhouse Pvt. Ltd. will comply with applicable
              third-party terms of agreement when using Licensed Application.
            </Typography>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              In Accordance with Section 9 of the 'Instructions for Minimum
              Terms of Developer's End-User Licence Agreement', both Apple and
              Google and their subsidiaries shall be third-party beneficiaries
              of this End User Licence Agreement and — upon Your acceptance of
              the terms and conditions of this Licence Agreement, both Apple and
              Google will have the right (and will be deemed to have accepted
              the right) to enforce this End User Licence Agreement against You
              as a third-party beneficiary thereof.
            </Typography>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              INTELLECTUAL PROPERTY RIGHTS
            </Heading>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Blacktail Mindhouse Pvt. Ltd. and the End-User acknowledge that,
              in the event of any third-party claim that the Licensed
              Application or the End-User's possession and use of that Licensed
              Application infringes on the third party's intellectual property
              rights, Blacktail Mindhouse Pvt. Ltd., and not the Services, will
              be solely responsible for the investigation, defence, settlement,
              and discharge or any such intellectual property infringement
              claims.
            </Typography>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              APPLICABLE LAW
            </Heading>
            <Typography
              fontFamily={"var(--man-500)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              This Licence Agreement is governed by the laws of India excluding
              its conflicts of law rules.
            </Typography>
          </LI>
        </OL>
      </Wrapper>
      <GlobalCss />
    </Container>
  );
};
function EndUserLicenseAgreement() {
  useDisableIntercom({});
  return <EndUserLicenseAgreementPage />;
}

export default EndUserLicenseAgreement;
const Heading = styled(DefaultTypography)`
  // text-decoration: underline;
  font-family: var(--man-700);
`;
const Typography = styled(DefaultTypography)`
  text-align: justify;
`;
const Container = styled(FlexContainer)`
  margin: 40px auto auto;
`;
const AlphaList = styled.li`
  list-style-type: lower-alpha;
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0px;
  }
`;
const Wrapper = styled(FlexDiv)`
  padding: ${(props) => {
    return props.showHeightGap ? "110px 0px" : "10px 0px";
  }};
  p {
    margin-bottom: 11px;
    &:last-child {
      margin-bottom: 0px;
    }
  }
`;
const OL = styled.ol`
  ${space}
  ol {
    padding-inline-start: 40px !important;
  }
  counter-reset: item;
  > li {
    padding-left: 0px;
    li {
      padding-left: 0px;
    }
    ${AlphaList} {
      padding-left: 0;
    }
    ${Heading} {
      margin-bottom: 25px;
    }
  }
`;
const UL = styled.ul`
  ${space}
  ul {
    padding-inline-start: 40px !important;
  }
`;
const LI = styled.li`
  display: block;
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0px;
  }
  position: relative;
  &:before {
    display: inline-block;
    float: left;
    margin-right: 5px;
    content: counters(item, ".") ".";
    counter-increment: item;
    font-size: 12px;
    font-family: var(--man-500);
    color: ${theme.colors.gray_100};
    @media only screen and (min-width: 640px) {
      font-size: 14px;
    }
    @media only screen and (min-width: 960px) {
      font-size: 16px;
    }
  }
`;
const Strong = styled.strong`
  font-size: inherit;
  font-family: inherit;
  color: inherit;
`;
const Address = styled.address`
  margin-bottom: 5px;
  font-style: italic;
`;
const SUBOL = styled(OL)`
  counter-reset: item;
`;
const GlobalCss = createGlobalStyle`
::marker {
    font-size: 12px;
    color: ${theme.colors.gray_100};
    font-family: 'Raleway',sans-serif;
    @media only screen and (min-width: 640px) {
    font-size: 14px;
    }
    @media only screen and (min-width: 960px) {
    font-size: 16px;
    }
}
  `;
